<template>
  <div>
    <el-row type="flex" justify="center">
      <el-col :xs="24" :sm="18" :md="14">
        <pin-code-keyboard v-if="!correctPinCode" @validPinCode="toggleSettings" />
        <template v-if="correctPinCode">
          <common-button
            id="logout-button"
            :accesskey="navigationAccessKeys.logout"
            class="secondary uppercase settings-logout"
            data-test-selector="logout-button"
            @click.prevent="logout"
          >
            {{ $t('welcome.logoutButton') }}
          </common-button>
          <h1 class="title">{{ $t('settings.catering.settingTitle') }}</h1>
          <span>{{ $t('settings.catering.bodyMessage') }}</span>
          <div class="main-settings">
            <div class="settings-section">
              <h2 class="sub-title">{{ $t('settings.catering.dateHeader') }}</h2>
              <el-date-picker
                v-model="date"
                :picker-options="pickerOptions"
                type="date"
                placeholder="Pick a day"
              >
              </el-date-picker>
            </div>
            <div class="settings-section">
              <h2 class="sub-title">{{ $t('settings.catering.officeHeader') }}</h2>
              <office-dropdown :selected="selectedOffice" @change="onOfficeSelected" />
            </div>
            <div class="settings-section">
              <h2 class="sub-title">{{ $t('settings.catering.dietsHeader') }}</h2>
              <diet-selection
                :display-description="true"
                :checked-values="diets"
                @checked="onDietChecked"
              />
            </div>
            <div class="settings-section">
              <h2 class="sub-title">{{ $t('settings.catering.mealHeader') }}</h2>
              <event-selection :selected-event="event" @change="onEventChanged" />
            </div>
            <div class="save-button">
              <common-button primary @click="saveSettings">
                {{ $t('settings.catering.saveButton') }}
              </common-button>
            </div>
          </div>
        </template>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import DietSelection from '@/components/catering/DietSelection';
import EventSelection from '@/components/catering/EventSelection';
import PinCodeKeyboard from '@/components/catering/PinCodeKeyboard';
import OfficeDropdown from '@/components/OfficeDropdown';
import { mapActions, mapState } from 'vuex';
import { NAVIGATION_ACCESS_KEYS, ROUTES, cateringSettingsDefaults } from '@/constants';
import CommonButton from '@/components/common/CommonButton';

const { OFFICE, EVENT, DIETS } = cateringSettingsDefaults;

export default {
  name: 'CateringSettings',
  components: {
    DietSelection,
    OfficeDropdown,
    PinCodeKeyboard,
    EventSelection,
    CommonButton,
  },
  data() {
    return {
      selectedOffice: OFFICE,
      diets: DIETS,
      event: EVENT,
      date: Date.now(),
      correctPinCode: false,
      navigationAccessKeys: NAVIGATION_ACCESS_KEYS.navbar,
      pickerOptions: {},
    };
  },
  async created() {
    this.correctPinCode = false;
    await this.getSettings();
  },
  computed: {
    ...mapState('catering', { settings: (state) => state.catering.settings }),
    ...mapState('events', { mealEvents: (state) => state.mealEvents }),
    ...mapState('offices', { offices: (state) => state.offices }),
    ...mapState('diets', { baseDiets: (state) => state.diets }),
  },
  watch: {
    catering() {
      const { error: settingsError, settings } = this.settings;
      if (!settingsError && settings) {
        this.selectedOffice = settings.office.id;
        this.diets = settings.diets.map((d) => d.id);
        this.event = settings.event.id;
      } else if (settingsError) {
        // We can throw a modal warning
        this.selectedOffice = 1;
        this.diets = [1, 2, 3];
        this.event = 1;
      }
    },
  },
  methods: {
    ...mapActions('catering', {
      setSettings: 'setCateringSettings',
      getSettings: 'getCateringSettings',
    }),
    onOfficeSelected(officeId) {
      this.selectedOffice = officeId;
    },
    onDietChecked(diets) {
      this.diets = diets;
    },
    onEventChanged(eventId) {
      this.event = eventId;
    },
    toggleSettings(isValid) {
      this.correctPinCode = isValid;
    },
    logout() {
      this.$auth0.logout();
    },
    async saveSettings() {
      try {
        const saveObj = this.generateSettingsObj();
        await this.setSettings(saveObj); // trigger action to vuex store
        await this.$alert(this.$t('settings.messages.settingsSaved'), {
          confirmButtonText: 'OK',
        });
        this.$router.push(ROUTES.CATERING);
      } catch (e) {
        console.error(e); // Display the root error of saving the settings only in console
        await this.$alert(this.$t('settings.messages.cateringSettingsError'), {
          confirmButtonText: 'OK',
        });
      }
    },
    generateSettingsObj() {
      const officeObj = this.offices.find((v) => this.selectedOffice === v.id) || {};
      const eventObj = this.mealEvents.find((v) => this.event === v.id) || {};
      const diets = this.diets.map((d) => {
        const diet = this.baseDiets.find((v) => d === v.id);
        if (diet) {
          const { name, id } = diet;
          return { name, id };
        }
        return;
      });
      return {
        office: { name: officeObj.name, id: officeObj.id },
        event: {
          name: eventObj.name,
          id: eventObj.id,
        },
        diets,
        date: new Date(this.date).toISOString(),
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';

.el-date-editor {
  max-width: 194px;
}

.main-settings {
  text-align: left;
  margin-left: 50px;
  margin-top: 5px;
  width: auto;
}
.settings-logout {
  float: right;
  margin-top: 15px;
}
.settings-section {
  padding-top: 5px;
  padding-bottom: 15px;
}
.save-button {
  padding: 20px;
}
.title {
  margin-left: 15px;
  font-weight: $bold-font;
  font-size: $title-font-size;
}
.sub-title {
  font-weight: $bold-font;
}
.title,
.sub-title {
  margin: 20px 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/Catering/CateringSettings.vue